import { createWrapper } from "@segment/analytics-consent-tools";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { getInvoiceIdFromCPEPath } from "../ClientPayments/utils";
import { CONSENT_CHANGED_EVENT } from "../modules/CookieConsent/constants";
import { getCurrentPreferences } from "../modules/CookieConsent/utils/consentManager";
import {
  getAdminSessionToken,
  getRequestingUserSessionUserId
} from "../services/sessionStorage";
import {
  BUILD_ENV,
  IS_DEV_ENV,
  IS_INTEGRATION_ENV
} from "../shared/constants/environment";
import { localStorageUtil } from "./localStorage";

const SEGMENT_CDN_URL = "https://analytics-cdn.wingspan.app";

const analytics = new AnalyticsBrowser();

const getCategories = (): Record<string, boolean> => {
  const { C0001MT, C0002AL } = getCurrentPreferences();
  return { C0001MT, C0002AL };
};

// Opt-in model: device-mode destinations whose categories are not consented
// are removed from the CDN settings before they load. Cloud-mode destinations
// still receive events stamped with the consent context for server-side filtering.
// Category → destination mapping is configured in the Segment workspace
// (Privacy → Consent Management).
const withConsent = createWrapper<AnalyticsBrowser>({
  getCategories,
  registerOnConsentChanged: setCategories => {
    document.addEventListener(CONSENT_CHANGED_EVENT, () => {
      setCategories(getCategories());
    });
  },
  shouldLoadSegment: ctx => ctx.load({ consentModel: "opt-in" })
});

export const loadSegment = (): void => {
  try {
    const shouldSend = shouldSendAnalyticsCall();
    if (shouldSend && process.env.REACT_APP_SEGMENT_API_KEY) {
      withConsent(analytics).load(
        {
          cdnURL: SEGMENT_CDN_URL,
          writeKey: process.env.REACT_APP_SEGMENT_API_KEY
        },
        { initialPageview: false }
      );

      page();
    }
  } catch (error) {
    if (IS_DEV_ENV || window.debugSegment || window.TEST_ENV) {
      console.error("Failed loading Segment.io");
      console.error(error);
    }
  }
};

export const SEGMENT_NOT_LOADED = "SEGMENT_NOT_LOADED";

export const getSegmentAnonymousIdFromLocalStorage = (): string => {
  const id =
    localStorageUtil.getItem("ajs_anonymous_id") || `"${SEGMENT_NOT_LOADED}"`;
  return id.split('"')[1];
};

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

function shouldSendAnalyticsCall() {
  if (window.forceSendSegment) {
    return true;
  }

  if (!IS_INTEGRATION_ENV && (IS_DEV_ENV || window.TEST_ENV)) {
    return false;
  }

  const isAdminSession = !!getAdminSessionToken();
  const isUserEmailIsWingspanRelated =
    window.userEmail && window.userEmail.includes("wingspan.app");

  return !isUserEmailIsWingspanRelated && !isAdminSession;
}

const buildEnv = BUILD_ENV;
const buildDate = new Date(
  `${process.env.REACT_APP_BUILD_TIMESTAMP}`
).toLocaleString(undefined, { hour12: false });
const buildCommit = process.env.REACT_APP_GIT_LATEST_COMMIT;

function getCommonFields() {
  const deviceType = getDeviceType();
  return {
    context: {
      wingspan: {
        // enabledFeatureFlags: getEnabledFeatureFlags(state),
        buildCommit,
        buildDate,
        buildEnv,
        deviceType
      }
    }
  };
}

export function page(
  name: string | undefined = undefined,
  properties: any = {}
) {
  const customProperties = {
    invoiceId: getInvoiceIdFromCPEPath(window.location.pathname)
  };
  const requestingUserId = getRequestingUserSessionUserId();
  const commonPageProps = {
    ...(requestingUserId ? { requestingUserId } : {})
  };
  const eventName = name;
  const eventProperties = {
    ...commonPageProps,
    ...properties,
    ...customProperties
  };
  const eventOptions = getCommonFields();
  const shouldSend = shouldSendAnalyticsCall();
  if (IS_DEV_ENV || window.debugSegment || window.TEST_ENV) {
    console.info(
      `Analytics Page call for ${getSegmentAnonymousIdFromLocalStorage()} ${
        shouldSend ? "sent" : "not sent"
      }: ${JSON.stringify([eventName, eventProperties, eventOptions])}`
    );
  }

  if (shouldSend) {
    analytics.page(eventName, eventProperties, eventOptions);
  }
}

export function identifyWithCommonFields(
  userId: string | undefined = undefined,
  traits: any = {}
) {
  try {
    const shouldSend = shouldSendAnalyticsCall();
    const eventTraits = { ...traits };
    const eventOptions = {
      ...getCommonFields(),
      integrations: {
        Intercom:
          window.intercomUserJwtAuthExperiment && window.intercomUserJwt
            ? { intercom_user_jwt: window.intercomUserJwt }
            : { user_hash: window.intercomUserHash }
      }
    };
    if (IS_DEV_ENV || window.debugSegment || window.TEST_ENV) {
      console.info(
        `Analytics Identify call for ${getSegmentAnonymousIdFromLocalStorage()} ${
          shouldSend ? "sent" : "not sent"
        }: ${JSON.stringify([userId, eventTraits, eventOptions])}`
      );
    }
    if (shouldSend) {
      analytics.identify(userId, eventTraits, eventOptions);
    }
  } catch (error) {
    console.error("IDENTIFY ERROR:", error);
  }
}

export function track(
  event: string | undefined = undefined,
  properties: any = {}
) {
  const shouldSend = shouldSendAnalyticsCall();
  const commonFields = getCommonFields();
  const requestingUserId = getRequestingUserSessionUserId();
  const commonTrackProps = {
    ...(requestingUserId ? { requestingUserId } : {})
  };
  const eventProperties = { ...commonTrackProps, ...properties };
  if (IS_DEV_ENV || window.debugSegment || window.TEST_ENV) {
    console.info(
      `Analytics Track call for ${getSegmentAnonymousIdFromLocalStorage()} ${
        shouldSend ? "sent" : "not sent"
      }: ${JSON.stringify([event, eventProperties, commonFields])}`
    );
  }

  if (shouldSend && event) {
    analytics.track(event, eventProperties, commonFields);
  }
}

export function reset() {
  // just wrap the function in our own fun(ction)
  if (IS_DEV_ENV || window.debugSegment || window.TEST_ENV) {
    console.info(`Analytics reset`);
    console.info("Analytics anon id", getSegmentAnonymousIdFromLocalStorage());
  }
  analytics.reset();
}

export function group(groupId: string, traits?: any) {
  const shouldSend = shouldSendAnalyticsCall();

  if (shouldSend) {
    analytics.group(groupId, traits);
  }
}
